/**
 * Das Angebotsanschreiben der Mühle.
 *
 * Wortlaut und Aufbau kommen vom Kunden und werden hier eins zu eins
 * abgebildet — nicht als Vorschlag, sondern als Hausform:
 *
 *   Sehr geehrte Damen und Herren,
 *
 *   wir freuen uns über Ihr Interesse an unseren Produkten und möchten
 *   Ihnen folgendes freibleibend anbieten:
 *
 *   Qualität/Mehltype:    Weizenmehl Type 550 lose
 *   Laufzeit:             Januar bis Dezember 2027
 *   Menge:                1.200 to
 *   Lieferung:            Frachtfrei Ihrem Werk in Hamburg, sukzessive
 *                         Abnahme von vollen LKW (min. 25to)
 *   Preis:                36,00 EURO/100 kg netto
 *   Zahlungsziel:         14 Tage, netto Kasse
 *
 *   Gern hören wir von Ihnen und verbleiben
 *
 * Erzeugt wird reiner Text zum Einfügen in eine Mail. Die Beschriftungen
 * stehen in einer festen Spalte statt auf Word-Tabulatoren — in einer Mail
 * gibt es keine Tabulatoren, und ungleiche Abstände sähen nach Versehen aus.
 *
 * Liegt in shared, weil Angebot und Schnellkalkulation denselben Text
 * erzeugen müssen. Zwei Fassungen desselben Briefs driften auseinander, und
 * dann bekommt derselbe Kunde je nach Weg eine andere Formulierung.
 */

import { formatCent } from "./money.js";

/** Beschriftungsspalte. Die längste Beschriftung ist „Qualität/Mehltype:". */
const SPALTE = 22;

const MONATE = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];

export interface Anschreibenposition {
  bezeichnung: string;
  /** Gebindetext, etwa „lose, Silofahrzeug". Leer, wenn ohne Bedeutung. */
  verpackung: string | null;
  menge_kg: number;
  /** Endpreis je 100 kg in ganzen Cent. */
  preis_cent: number;
}

export interface AnschreibenDaten {
  verbindlichkeit: "verbindlich" | "freibleibend";
  positionen: Anschreibenposition[];
  /** Kontraktlaufzeit als ISO-Datum. Ohne Kontrakt entfällt die Zeile. */
  laufzeit_von?: string | null;
  laufzeit_bis?: string | null;
  lieferbedingung: "ab_werk" | "frachtfrei_empfaenger";
  /** Ort des Kunden — „Frachtfrei Ihrem Werk in Hamburg". */
  kunde_ort?: string | null;
  /** Standort der Mühle für die Selbstabholung. */
  werk_ort?: string | null;
  /** Kontrakt mit Abruf: „sukzessive Abnahme von vollen LKW". */
  sukzessive?: boolean;
  teilpartie_min_kg?: number | null;
  zahlungsziel_tage: number;
  /** Standardtexte aus den Einstellungen. */
  fusstext?: string | null;
  agb_hinweis?: string | null;
  /** Firmenkopf: Pflichtangaben nach § 125a HGB, zeilenweise. */
  absender?: string[];
}

/** "1.200" — deutsche Tausendertrennung, ohne Einheit. */
const tonnen = (kg: number) => (kg / 1000).toLocaleString("de-DE", { maximumFractionDigits: 3 });

const feld = (bezeichnung: string, wert: string) => `${`${bezeichnung}:`.padEnd(SPALTE)}${wert}`;

/**
 * „Januar bis Dezember 2027", bei Jahreswechsel „Oktober 2026 bis März 2027".
 *
 * Die Jahreszahl steht nur einmal, wenn sie sich nicht ändert — so schreibt
 * man es, und so steht es in der Vorlage.
 */
export function laufzeitText(von: string | null, bis: string | null): string | null {
  const a = von ? new Date(von) : null;
  const b = bis ? new Date(bis) : null;
  if (!a || Number.isNaN(a.getTime())) return null;

  const teil = (d: Date) => `${MONATE[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
  if (!b || Number.isNaN(b.getTime())) return `ab ${teil(a)}`;

  if (a.getUTCFullYear() !== b.getUTCFullYear()) return `${teil(a)} bis ${teil(b)}`;
  if (a.getUTCMonth() === b.getUTCMonth()) return teil(a);
  return `${MONATE[a.getUTCMonth()]} bis ${MONATE[b.getUTCMonth()]} ${b.getUTCFullYear()}`;
}

/** „Frachtfrei Ihrem Werk in Hamburg, sukzessive Abnahme von vollen LKW (min. 25to)" */
export function lieferungText(d: AnschreibenDaten): string {
  const teile: string[] = [];

  if (d.lieferbedingung === "ab_werk") {
    teile.push(`Ab Werk${d.werk_ort ? ` ${d.werk_ort}` : ""}, Selbstabholung`);
  } else {
    teile.push(`Frachtfrei Ihrem Werk${d.kunde_ort ? ` in ${d.kunde_ort}` : ""}`);
  }

  if (d.sukzessive)
    teile.push(
      "sukzessive Abnahme von vollen LKW" +
        (d.teilpartie_min_kg ? ` (min. ${tonnen(d.teilpartie_min_kg)}to)` : ""),
    );

  return teile.join(", ");
}

export function baueAnschreiben(d: AnschreibenDaten): string {
  const z: string[] = [];

  z.push("Sehr geehrte Damen und Herren,");
  z.push("");
  z.push(
    "wir freuen uns über Ihr Interesse an unseren Produkten und möchten Ihnen " +
      `folgendes ${d.verbindlichkeit} anbieten:`,
  );
  z.push("");

  const laufzeit = laufzeitText(d.laufzeit_von ?? null, d.laufzeit_bis ?? null);

  /*
   * „Weizenmehl Type 550 lose" — nicht „lose, Silofahrzeug".
   *
   * Die Gebindebezeichnung der Stammdaten trägt oft einen erklärenden
   * Zusatz hinter dem Komma. In einer Tabelle hilft der; in der Zeile
   * „Qualität/Mehltype" ist er Beiwerk und macht aus der Ware einen
   * Halbsatz.
   */
  const ware = (p: Anschreibenposition) =>
    [p.bezeichnung, p.verpackung?.split(",")[0].trim()].filter(Boolean).join(" ");

  if (d.positionen.length === 1) {
    /*
     * Der Regelfall und die Vorlage: Ware, Laufzeit, Menge, Lieferung,
     * Preis, Zahlungsziel — in genau dieser Reihenfolge. Sie erzählt das
     * Angebot der Reihe nach: was, wie lange, wie viel, wohin, zu welchem
     * Preis, zahlbar wann. Nicht umsortieren.
     */
    const p = d.positionen[0];
    z.push(feld("Qualität/Mehltype", ware(p)));
    z.push("");
    if (laufzeit) {
      z.push(feld("Laufzeit", laufzeit));
      z.push("");
    }
    z.push(feld("Menge", `${tonnen(p.menge_kg)} to`));
    z.push("");
    z.push(feld("Lieferung", lieferungText(d)));
    z.push("");
    z.push(feld("Preis", `${formatCent(p.preis_cent)} EURO/100 kg netto`));
    z.push("");
    z.push(feld("Zahlungsziel", `${d.zahlungsziel_tage} Tage, netto Kasse`));
    z.push("");
  } else {
    /*
     * Mehrere Mehltypen deckt die Vorlage nicht ab — sie ist auf einen
     * Artikel geschrieben. Jede Lösung weicht also ab; diese weicht so
     * wenig wie möglich ab: Ware, Menge und Preis stehen nummeriert
     * beieinander, damit die Menge zu ihrem Artikel gehört, und was für
     * das ganze Angebot gilt, steht einmal darunter.
     */
    for (const [i, p] of d.positionen.entries()) {
      z.push(`${i + 1})`.padEnd(4) + feld("Qualität/Mehltype", ware(p)));
      z.push("    " + feld("Menge", `${tonnen(p.menge_kg)} to`));
      z.push("    " + feld("Preis", `${formatCent(p.preis_cent)} EURO/100 kg netto`));
      z.push("");
    }
    if (laufzeit) {
      z.push(feld("Laufzeit", laufzeit));
      z.push("");
    }
    z.push(feld("Lieferung", lieferungText(d)));
    z.push("");
    z.push(feld("Zahlungsziel", `${d.zahlungsziel_tage} Tage, netto Kasse`));
    z.push("");
  }

  if (d.fusstext) {
    z.push(d.fusstext);
    z.push("");
  }
  if (d.agb_hinweis) {
    z.push(d.agb_hinweis);
    z.push("");
  }

  z.push("Gern hören wir von Ihnen und verbleiben");
  z.push("");
  z.push("mit freundlichen Grüßen");

  // Der Firmenkopf ist keine Höflichkeit: Ein Angebot ist ein Geschäftsbrief,
  // und bei einer GmbH & Co. KG verlangt § 125a HGB die Angaben zur
  // Komplementärin auf jedem einzelnen.
  const absender = (d.absender ?? []).filter(Boolean);
  if (absender.length) {
    z.push("");
    z.push(...absender);
  }

  return z.join("\n");
}
